import { ChangeDetectionStrategy, Component, OnDestroy, computed, inject, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { distinctUntilChanged, filter, map } from 'rxjs';
import {
  CompaniesContentComponent,
  DevicesContentComponent,
  InstallationsContentComponent,
  LocationsContentComponent,
} from '../../components';
import { canViewInstallations } from '@core/helpers/installation-access.helper';
import { AuthStore } from '@root/app/store/auth.store';
import { SearchStore } from '@root/app/store/search.store';
import { UserStore } from '@root/app/store/user.store';
import { TabViewModel } from '@core/models/tab-view.model';
import { BreadcrumbItem } from '@custom-components/general/breadcrumb/breadcrumb.component';
import { PageBodyComponent } from '@custom-components/layout/page-body/page-body.component';
import { PageHeaderComponent } from '@custom-components/layout/page-header/page-header.component';
import { TabBarComponent } from '@custom-components/layout/tab-bar/tab-bar.component';

@Component({
  selector: 'xemex-search-page',
  imports: [
    NgTemplateOutlet,
    TranslateModule,
    PageHeaderComponent,
    PageBodyComponent,
    TabBarComponent,
    CompaniesContentComponent,
    DevicesContentComponent,
    InstallationsContentComponent,
    LocationsContentComponent,
  ],
  templateUrl: './search-page.component.html',
  styleUrl: './search-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchPageComponent implements OnDestroy {
  readonly searchStore = inject(SearchStore);
  private readonly route = inject(ActivatedRoute);
  private readonly translate = inject(TranslateService);
  private readonly userStore = inject(UserStore);
  private readonly authStore = inject(AuthStore);

  private readonly searchText$ = this.route.queryParamMap.pipe(
    map(params => params.get('text') ?? ''),
    filter(text => !!text),
    distinctUntilChanged(),
  );

  protected readonly searchText = toSignal(this.searchText$, { initialValue: '' });
  protected readonly selectedTab = signal('partners');

  private readonly showInstallations = computed(() =>
    canViewInstallations(this.userStore.user(), this.authStore.permissions()),
  );
  // The search answers installations to anyone holding the feature, so they are dropped here
  // for everyone else rather than counted in the total.
  protected readonly installations = computed(() =>
    this.showInstallations() ? this.searchStore.searchs().installations : [],
  );

  protected readonly totalResults = computed(() => {
    const { partners, locations, devices } = this.searchStore.searchs();
    return partners.length + locations.length + devices.length + this.installations().length;
  });

  protected readonly tabs = computed<TabViewModel[]>(() => {
    const loading = this.searchStore.isLoading();
    const { partners, locations, devices } = this.searchStore.searchs();
    return [
      {
        index: 0,
        title: 'search.companies.title',
        route: 'partners',
        count: loading ? undefined : partners.length,
      },
      {
        index: 1,
        title: 'search.locations.title',
        route: 'locations',
        count: loading ? undefined : locations.length,
      },
      {
        index: 2,
        title: 'search.devices.title',
        route: 'devices',
        count: loading ? undefined : devices.length,
      },
      ...(this.showInstallations()
        ? [
            {
              index: 3,
              title: 'search.installations.title',
              route: 'installations',
              count: loading ? undefined : this.installations().length,
            },
          ]
        : []),
    ];
  });

  protected readonly selectedTabIndex = computed(
    () => this.tabs().find(tab => tab.route === this.selectedTab())?.index ?? 0,
  );

  get breadcrumbs(): BreadcrumbItem[] {
    return [
      { label: this.translate.instant('sidebarMenu.home'), routerLink: '/' },
      { label: this.translate.instant('search.breadcrumb') },
    ];
  }

  constructor() {
    this.searchStore.searchsMethod(this.searchText$);
  }

  ngOnDestroy(): void {
    this.resetHeaderSearchBox();
  }

  onTabChange(route: string): void {
    this.selectedTab.set(route);
  }

  resetHeaderSearchBox(): void {
    const headerSearchElement = document.getElementById('headerSearch') as HTMLInputElement | null;
    if (headerSearchElement) {
      headerSearchElement.value = '';
    }
  }
}
